import { encode } from 'html-entities'
import {
	prettyPrintJson as _prettyPrintJson,
	FormatOptions,
} from 'pretty-print-json'

export {
	escapeQuotes,
	capitalizeFirstLetter,
	uncapitalizeFirstLetter,
	sentenceCase,
	parseRegExp,
	calculateNextFreeName,
	generateRandomString,
} from '../util/string-util.js'
export type TextConversionType = 'ascii' | 'utf-8' | 'base64' | 'hex'
export type ConversionType = TextConversionType | 'binary'

export const encodeUrl = encodeURIComponent
export const decodeUrl = decodeURIComponent

export function convert(
	input: string,
	from: TextConversionType,
	to: TextConversionType,
): string
export function convert(
	input: ArrayBuffer,
	from: 'binary',
	to: TextConversionType,
): string
export function convert(
	input: string,
	from: TextConversionType,
	to: 'binary',
): ArrayBuffer
export function convert(
	input: ArrayBuffer,
	from: 'binary',
	to: 'binary',
): ArrayBuffer
export function convert(
	input: string | ArrayBuffer,
	from: ConversionType,
	to: ConversionType,
): string | ArrayBuffer {
	return '' // method is overridden in Frontend/Backend bridges
}

export function truncateWithEllipsis(text: string, maxLength: number) {
	if (text.length <= maxLength) return text
	return text.substring(0, maxLength - 3) + '...'
}

export function isValidIban(iban: string) {
	if (!iban) return false
	iban = iban.replace(/\s+/g, '').toUpperCase()
	const countryLengths = {
		AD: 24,
		AE: 23,
		AL: 28,
		AO: 25,
		AT: 20,
		AZ: 28,
		BA: 20,
		BE: 16,
		BF: 27,
		BG: 22,
		BH: 22,
		BI: 16,
		BJ: 28,
		BR: 29,
		CH: 21,
		CI: 28,
		CM: 27,
		CR: 22,
		CV: 25,
		CY: 28,
		CZ: 24,
		DE: 22,
		DK: 18,
		DO: 28,
		DZ: 24,
		EE: 20,
		ES: 24,
		FI: 18,
		FO: 18,
		FR: 27,
		GB: 22,
		GE: 22,
		GI: 23,
		GL: 18,
		GR: 27,
		GT: 28,
		HR: 21,
		HU: 28,
		IE: 22,
		IL: 23,
		IR: 26,
		IS: 26,
		IT: 27,
		JO: 30,
		KW: 30,
		KZ: 20,
		LB: 28,
		LI: 21,
		LT: 20,
		LU: 20,
		LV: 21,
		MC: 27,
		MD: 24,
		ME: 22,
		MG: 27,
		MK: 19,
		ML: 28,
		MR: 27,
		MT: 31,
		MU: 30,
		MZ: 25,
		NL: 18,
		NO: 15,
		PK: 24,
		PL: 28,
		PS: 29,
		PT: 25,
		QA: 29,
		RO: 24,
		RS: 22,
		SA: 24,
		SE: 24,
		SI: 19,
		SK: 24,
		SM: 27,
		SN: 28,
		TN: 24,
		TR: 26,
		VG: 24,
		XK: 20,
	}

	const country = iban.substring(0, 2)
	if (iban.length != countryLengths[country as keyof typeof countryLengths])
		return false

	iban = iban.substring(4) + iban.substring(0, 4)
	iban = iban.replace(/[A-Z]/g, (char) => (char.charCodeAt(0) - 55).toString())

	let remainder = 0
	for (let i = 0; i < iban.length; i++) {
		const digit = parseInt(iban.charAt(i))
		remainder = (remainder * 10 + digit) % 97
	}

	return remainder == 1
}

export function encodeHtml(text: string) {
	return encode(text)
}

export function prettyPrintJson(value: any, options?: FormatOptions) {
	return _prettyPrintJson.toHtml(value, options)
}

export function convertToSingular(word: string) {
	if (word.endsWith('ies')) return word.substring(0, word.length - 3) + 'y'
	if (word.endsWith('s')) return word.substring(0, word.length - 1)
	if (word.match(/children$/i)) return word.substring(0, word.length - 3) + 'd'
	return word
}

export function concatWithSpace(...args: string[]) {
	return concatWithSeparator(' ', ...args)
}

export function concatWithSeparator(separator: string, ...args: string[]) {
	return args.filter(Boolean).join(separator)
}

export function createUuid() {
	return crypto.randomUUID()
}

export function formatAsCurrency(value: number, locale = 'de-CH') {
	return formatAsDecimalNumber(value, 2, locale)
}

export function formatAsDecimalNumber(
	value: number,
	decimals: number,
	locale = 'de-CH',
) {
	const numberFormat = new Intl.NumberFormat(locale, {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	})
	return numberFormat.format(value)
}
